import { useCallback, useMemo, useState } from "react";

import { Badge, Button, CardFooter } from "@fluentui/react-components";
import { WarningFilled } from "@fluentui/react-icons";

// import { LOCATION_STATES, ROUTES } from "library/constants";
import { isWithinInterval } from "date-fns";
import { useAtomValue } from "jotai";
import { EApprovalStatus, ETaxonomy, ICardBenefitPackage } from "library/types";
import { formatNumber } from "library/utils";
import { useTranslation } from "react-i18next";
import { benefitCardImageAtom } from "store";

import { AppName } from "components/AppName/AppName";
import { ButtonWithTooltip } from "components/ButtonWithTooltip/ButtonWithTooltip";
import { DocumentsDialog } from "components/DocumentsDialog/DocumentsDialog";
import { CheckerboardRevealCard } from "components/effects/CheckerboardReveal";
import { Flex, StyledTooltip } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";
import { useTaxonomyOptions } from "hooks/useTaxonomyOptions";

import PlaceholderImg from "assets/download2.png";
import HbhLogo from "assets/hbhLogo.svg";

import { BenefitAction } from "./components/BenefitAction";
import { BenefitDetailsDialog } from "./components/BenefitDetailsDialog";
import {
  BenefitImage,
  CardBody,
  CategoryAndPriceWrap,
  Desc,
  HeaderContent,
  PackageName,
  PlaceholderImageBox,
  StyledCard,
  StyledCardHeader,
  StyledMoneyIcon,
} from "./style-new";

type Props = {
  benefitPackage?: ICardBenefitPackage;

  selected?: boolean;
  eligible?: boolean;
  waitingForApproval?: boolean;
  waitingForCancel?: boolean;
  packageFormSameImplementationsInPendingStatus?: string;
  upgradeState?: {
    enrolledPackageName: string;
    upgradePackageName: string;
  };
};

export const BenefitCard = ({
  benefitPackage,
  selected,
  upgradeState,
  eligible,
  waitingForApproval,
  packageFormSameImplementationsInPendingStatus,
  waitingForCancel,
}: Props) => {
  const { t } = useTranslation();
  const theme = useAppTheme();

  const withImage = useAtomValue(benefitCardImageAtom);

  const [showDetails, setShowDetails] = useState(false);
  const [showBenefitDocuments, setShowBenefitDocuments] = useState(false);

  const setBenefitDocumentOpenState = useCallback((state) => {
    setShowBenefitDocuments(state);
  }, []);

  const setBenefitDetailsOpenState = useCallback((state) => {
    setShowDetails(state);
  }, []);

  const hasRejectedStatus =
    benefitPackage?.approveEnrollStatusCode === EApprovalStatus.REJECTED ||
    benefitPackage?.cancelEnrollApprovalStatusCode === EApprovalStatus.REJECTED;

  const packageIsExpired = !isWithinInterval(new Date(), {
    start: new Date(benefitPackage?.validFrom || ""),
    end: new Date(benefitPackage?.validTo || ""),
  });

  const currencyOptions = useTaxonomyOptions(ETaxonomy.Currency);
  const paymentMethodOptions = useTaxonomyOptions(ETaxonomy.PaymentMethod);

  const benefitPriceForEmployee = useMemo(() => {
    let paidByEmployee: number;
    const isPercentageValue = benefitPackage?.usingPercentagesForPaidBy;
    if (isPercentageValue) {
      paidByEmployee = ((benefitPackage?.price || 0) / 100) * (benefitPackage?.paidByEmployee || 0);
    } else {
      paidByEmployee = benefitPackage?.paidByEmployee || 0;
    }

    return formatNumber(paidByEmployee);
  }, [
    benefitPackage?.paidByEmployee,
    benefitPackage?.price,
    benefitPackage?.usingPercentagesForPaidBy,
  ]);

  const currency = currencyOptions?.find((item) => item.value === benefitPackage?.currencyCode);
  const paymentMethod = paymentMethodOptions?.find(
    (item) => item.value === benefitPackage?.paymentMethod
  );

  const benefitPriceLabel = t("hbh.benefitPrice.label");

  const benefitStatusConfig = (() => {
    const enrolled = benefitPackage?.dateOfEnrollment;
    const inReview = waitingForApproval || waitingForCancel;
    const canceled = benefitPackage?.dateOfCancellation && !inReview;
    if (canceled) {
      return {
        text: t("hbh.cancelled.label"),
        color: theme.palette.orange,
        background: theme.palette.redLight,
      };
    }
    if (packageIsExpired) {
      return {
        text: t("hbh.expired.label"),
        color: theme.palette.orange,
        background: theme.palette.redLight,
      };
    }
    if (eligible && !inReview) {
      if (upgradeState) {
        return {
          text: t("hbh.eligible.label"),
          color: theme.palette.purpleDark,
          background: theme.palette.purpleLight,
        };
      }
      return {
        text: t("hbh.eligible.label"),
        color: theme.palette.purpleDark,
        background: theme.palette.purpleLight,
      };
    }
    if (enrolled && !inReview) {
      return {
        text: t("hbh.enrolled.label"),
        color: theme.palette.orange,
        background: theme.palette.greenLight,
      };
    }
    if (inReview) {
      return {
        text: t("hbh.inReview.label"),
        color: theme.palette.orange,
        background: theme.palette.yellow,
      };
    }

    return {
      text: "TODO",
      color: theme.palette.white,
      background: theme.palette.white,
    };
  })();

  const benefitCategoryLogo =
    benefitPackage?.topLevelBenefitCategoryBackgroundPicturePath || HbhLogo;

  return (
    <>
      <StyledCard
        $isSelected={selected}
        $canceled={!!benefitPackage?.dateOfCancellation}
        $expired={packageIsExpired}
      >
        <CheckerboardRevealCard>
          <StyledCardHeader
            header={
              <HeaderContent withImage={withImage}>
                <CategoryAndPriceWrap withImage={withImage}>
                  <img
                    src={benefitCategoryLogo}
                    alt="category"
                    style={{
                      width: "31px",
                      height: "31px",
                      borderRadius: "5px",
                      boxShadow: withImage ? "0px 4px 11px 0px #00000017" : "",
                      objectFit: "cover",
                      border: `1.5px solid ${theme.palette.themePrimary}`,
                    }}
                  />

                  <ButtonWithTooltip
                    style={{
                      minWidth: 0,
                      fontSize: "12px",
                      flexWrap: "wrap",
                      textAlign: "left",
                      ...(withImage
                        ? {
                            background: theme.palette.purpleDark,
                            color: theme.palette.white,
                            boxShadow: "0px 4px 11px 0px #00000017",
                            padding: "3px 10px",
                            borderRadius: "5px",
                          }
                        : {}),
                    }}
                    tooltipContent={`${benefitPriceLabel}: ${benefitPriceForEmployee} ${
                      currency?.text ?? ""
                    } / ${paymentMethod?.text ?? ""}`}
                    icon={<StyledMoneyIcon withImage={withImage} />}
                  >
                    {benefitPriceForEmployee} {currency?.codeShort ?? ""}
                    <br />
                    {paymentMethod?.text ?? ""}
                  </ButtonWithTooltip>
                </CategoryAndPriceWrap>
                {withImage ? null : (
                  <BenefitAction
                    // withShadow
                    benefitPackage={benefitPackage}
                    eligible={eligible}
                    upgradeState={upgradeState}
                    waitingForApproval={waitingForApproval}
                    waitingForCancel={waitingForCancel}
                    packageFormSameImplementationsInPendingStatus={
                      packageFormSameImplementationsInPendingStatus
                    }
                    packageIsExpired={packageIsExpired}
                  />
                )}
              </HeaderContent>
            }
          />
          <CardBody>
            {withImage &&
              (benefitPackage?.backgroundPicturePath ? (
                <BenefitImage
                  src={benefitPackage?.backgroundPicturePath || PlaceholderImg}
                  alt={benefitPackage?.name}
                  loading="lazy"
                />
              ) : (
                <PlaceholderImageBox id="placeholderImageBox">
                  <AppName showOnMobile />
                </PlaceholderImageBox>
              ))}
            <Flex
              $justify="space-between"
              wrap="nowrap"
              align="center"
              style={{
                height: "32px",
              }}
            >
              <StyledTooltip content={benefitPackage?.name || ""} relationship="description">
                <PackageName
                  block
                  truncate
                  weight="bold"
                  style={{
                    marginTop: withImage ? "0" : "10px",
                    overflow: "hidden",
                    width: withImage ? "60%" : "100%",
                    display: "block",
                  }}
                >
                  {`${benefitPackage?.benefitCategoryName} - ${benefitPackage?.name}`}
                </PackageName>
              </StyledTooltip>
              {withImage && (
                <BenefitAction
                  upgradeState={upgradeState}
                  benefitPackage={benefitPackage}
                  eligible={eligible}
                  waitingForApproval={waitingForApproval}
                  waitingForCancel={waitingForCancel}
                  packageFormSameImplementationsInPendingStatus={
                    packageFormSameImplementationsInPendingStatus
                  }
                  packageIsExpired={packageIsExpired}
                />
              )}
            </Flex>
            <StyledTooltip
              relationship="label"
              content={{
                children: benefitPackage?.description,
              }}
            >
              <Desc>{benefitPackage?.description}</Desc>
            </StyledTooltip>
          </CardBody>

          <CardFooter
            style={{
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "10px",
            }}
          >
            <Button
              onClick={() => {
                setBenefitDetailsOpenState(true);
              }}
              style={{
                color: hasRejectedStatus ? theme.palette.redDark : theme.palette.purpleDark,
                textDecoration: "underline",
                paddingLeft: "0",
              }}
              iconPosition="after"
              icon={hasRejectedStatus ? <WarningFilled color={theme.palette.redDark} /> : null}
              appearance="transparent"
            >
              {t("hbh.viewDetails.label")}
            </Button>
            <Badge
              appearance="filled"
              style={{
                background: benefitStatusConfig?.background,
                color: benefitStatusConfig?.color,
                padding: "12px 15px",
              }}
            >
              {benefitStatusConfig?.text}
            </Badge>
          </CardFooter>
        </CheckerboardRevealCard>
      </StyledCard>

      {showBenefitDocuments && benefitPackage && (
        <DocumentsDialog
          title={t("hbh.benefitDocuments.label")}
          open={showBenefitDocuments}
          setOpen={setBenefitDocumentOpenState}
          documents={benefitPackage?.enrollmentDocuments || []}
        />
      )}
      {showDetails && benefitPackage && (
        <BenefitDetailsDialog
          open={showDetails}
          setOpen={setBenefitDetailsOpenState}
          benefitPackage={benefitPackage}
          formattedPrice={`${benefitPriceForEmployee} ${currency?.codeShort ?? ""} · ${paymentMethod?.text ?? ""}`}
          benefitStatusConfig={benefitStatusConfig}
          eligible={eligible}
        />
      )}
    </>
  );
};
